import React from "react"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AlertCircle } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"

interface AuthorizationModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (authKey: string) => Promise<void>
    title: string
    description: string
}

export function AuthorizationModal({ isOpen, onClose, onConfirm, title, description }: AuthorizationModalProps) {
    const [authKey, setAuthKey] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async () => {
        if (!authKey.trim()) {
            setError("Authorization key is required")
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            await onConfirm(authKey)
            // If we get here, it was successful
            setAuthKey("")
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unknown error occurred")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setAuthKey("")
        setError(null)
        onClose()
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                    <p className="mb-4 text-sm text-gray-600">{description}</p>

                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <Input
                        type="password"
                        placeholder="Enter authorization key"
                        value={authKey}
                        onChange={(e) => setAuthKey(e.target.value)}
                        className="w-full"
                        autoFocus
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? "Processing..." : "OK"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
